import { useTranslation } from 'react-i18next';
import MultiStepForm from 'components/shared/MultiStepForm/MultiStepForm';
import {
  ControlledAutoComplete,
  ControlledWorkAgreementSelect
} from 'components/shared/ControlledFields';
import { orderBy } from 'lodash';
import { useGetArrangements, useGetTravelCompensationType } from 'queries';
import { useEffect, useRef } from 'react';
import {
  arrangementTypesToOption,
  trvSetToOption
} from 'components/admin-portal/Tables/TravelCosts/Distances/utils';
import { type ArrangementAndCompensationSectionProps } from '../types';

const ArrangementAndCompensationSection = ({
  rowData,
  control,
  setValue,
  employeeId,
  selectedArrangementType
}: ArrangementAndCompensationSectionProps) => {
  const { t } = useTranslation('common');
  const prevArrangementTypeRef = useRef(selectedArrangementType);

  const { data: arrangementTypes } = useGetTravelCompensationType();

  const { data: arrangements } = useGetArrangements(arrangementTypes ?? [], {
    enabled: !!arrangementTypes
  });

  const filteredArrangements = arrangements?.filter(
    (arrangement) =>
      selectedArrangementType?.id &&
      arrangement.compensationTypeId === selectedArrangementType.id
  );

  useEffect(() => {
    if (rowData && arrangementTypes && arrangements) {
      setValue('waNr', rowData.waId ?? null);
      setValue(
        'arrangementTypes',
        arrangementTypesToOption(
          rowData,
          arrangementTypes,
          'compensationTypeName'
        )
      );
      setValue(
        'trvSet',
        trvSetToOption(rowData, arrangements, 'trvSetDescription')
      );
    }
  }, [rowData, arrangementTypes, arrangements]);

  // clear trvSetDescription when selectedArrangementType changes
  useEffect(() => {
    const prevId = prevArrangementTypeRef.current?.id;
    const newId = selectedArrangementType?.id;

    if (prevId && newId && prevId !== newId) {
      const props = [
        'trvSet',
        'postalCode',
        'city',
        'streetName',
        'houseNumber',
        'countryCode',
        'distanceKm',
        'travelExpensesNominal'
      ];
      props.forEach((prop) => setValue(prop, null));
    }

    prevArrangementTypeRef.current = selectedArrangementType;
  }, [selectedArrangementType, setValue]);

  return (
    <MultiStepForm.Section
      title={t('travelArrangements.arrangementAndCompensation')}
      validateFields={['arrangementTypes', 'trvSet']}
    >
      <ControlledWorkAgreementSelect
        name="waNr"
        control={control}
        label={t('employment')}
        allLabel={t('allWorkAgreements')}
        employeeId={employeeId}
      />
      <ControlledAutoComplete
        name="arrangementTypes"
        control={control}
        variant="outlined"
        label={t('travelArrangements.arrangementTypes')}
        options={orderBy(arrangementTypes, ['name'], ['asc'])}
        idProperty="id"
        optionLabelProp="name"
        fullWidth
      />
      <ControlledAutoComplete
        name="trvSet"
        control={control}
        variant="outlined"
        label={t('travelArrangements.compensationTypeName')}
        options={orderBy(filteredArrangements, ['description'], ['asc'])}
        idProperty="trvSetId"
        optionLabelProp="description"
        fullWidth
      />
    </MultiStepForm.Section>
  );
};

export default ArrangementAndCompensationSection;
